import React from 'react';

import { useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { IconButton } from '@atlaskit/button/new';
import CalendarFilledIcon from '@atlaskit/icon/core/migration/calendar--calendar-filled';
import ChartTrendIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import PeopleIcon from '@atlaskit/icon/core/migration/people-group--people';

import { ICON_SIZE_MAPPER } from './constants';
import messages from './messages';
import { type ResponderAvatarProps, type ResponderAvatarWithDefaultProps } from './types';

type AvatarComponent = React.VFC<ResponderAvatarProps>;

const AvatarWithDefault = ({
	responder,
	size,
	avatarRef,
	labelDescriptor,
	iconComponent: IconComponent,
	...props
}: ResponderAvatarWithDefaultProps) => {
	const intlApi = useIntl();

	const avatarUrl = 'avatarUrl' in responder ? responder.avatarUrl : undefined;

	if (avatarUrl) {
		return (
			<Avatar
				label={intlApi.formatMessage(labelDescriptor)}
				{...props}
				ref={avatarRef}
				size={size}
				src={avatarUrl}
			/>
		);
	}

	return (
		<Avatar {...props} ref={avatarRef} label={intlApi.formatMessage(labelDescriptor)} size={size}>
			{props.onClick ? (
				<IconButton
					label={props.label}
					onClick={props.onClick}
					isDisabled={props.isDisabled}
					tabIndex={props.tabIndex}
					testId={props.testId}
					analyticsContext={props.analyticsContext}
					icon={(iconProps) => <IconComponent {...iconProps} size={ICON_SIZE_MAPPER[size]} />}
				/>
			) : (
				<IconComponent
					label={intlApi.formatMessage(labelDescriptor)}
					size={ICON_SIZE_MAPPER[size]}
				/>
			)}
		</Avatar>
	);
};

const UserResponderAvatar: AvatarComponent = ({ responder, size, avatarRef, ...props }) => {
	const { formatMessage } = useIntl();

	return (
		<Avatar
			ref={avatarRef}
			label={formatMessage(messages.user)}
			{...props}
			size={size}
			src={'avatarUrl' in responder ? responder.avatarUrl : undefined}
		/>
	);
};

export const LoadedResponderAvatar: AvatarComponent = (props) => {
	const responder = props.responder;

	switch (responder.type) {
		case 'user':
			return <UserResponderAvatar {...props} />;
		case 'team':
		case 'group':
			return (
				<AvatarWithDefault labelDescriptor={messages.team} iconComponent={PeopleIcon} {...props} />
			);
		case 'schedule':
			return (
				<AvatarWithDefault
					labelDescriptor={messages.schedule}
					iconComponent={CalendarFilledIcon}
					{...props}
				/>
			);
		case 'escalation':
			return (
				<AvatarWithDefault
					labelDescriptor={messages.escalation}
					iconComponent={ChartTrendIcon}
					{...props}
				/>
			);
	}
};
